import React, { useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { HorizontalTabsContainer } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/horizontalTabsContainer/HorizontalTabsContainer'
import { MessengerUtilityItemList } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/messengerUtilityItemList/MessengerUtilityItemList'
import { TemplateEditor } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/templateEditor/TemplateEditor'
import { decodeHtmlEntities } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../../../data/store'
import { setToastContent } from '../../../../data/ltToastAutoActions'
import { InsertMessageOptions } from '../../../BaseChat'
import {
  useAddTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplatesQuery,
  useUpdateTemplateMutation,
} from '../../../data/messagesApi'
import { ProviderTemplateHeader } from '../../templates/ProviderTemplateHeader'
import MessengerUtilityItemContainer from '../MessengerUtilityItemContainer'
import MessengerUtilityItemContentContainer from '../MessengerUtilityItemContentContainer'

type MessengerUtilityItemTemplatesProps = {
  onSendPressed: () => void
  insertMessage: (message: string, opts?: InsertMessageOptions) => void
}

const MessengerUtilityItemTemplates: React.FC<MessengerUtilityItemTemplatesProps> = ({
  onSendPressed,
  insertMessage,
}) => {
  const { isSmartTemplatesEnabled } = useFlags()
  const [templateIdToDelete, setTemplateIdToDelete] = useState<string | null>(null)
  const [templateIdToEdit, setTemplateIdToEdit] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showTemplateEditor, setShowTemplateEditor] = useState(false)
  const [addTemplate] = useAddTemplateMutation()
  const [deleteTemplate] = useDeleteTemplateMutation()
  const [updateTemplate] = useUpdateTemplateMutation()
  const providerId = useSelector(getAuthUserId)
  const appDispatch = useAppDispatch()
  const { data: templates } = useGetTemplatesQuery(
    { providerId },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  )
  const sortedTemplates = templates ? [...templates] : []
  sortedTemplates.sort((a, b) => {
    const bDate = new Date(b.create_date)
    const aDate = new Date(a.create_date)
    return bDate.getTime() - aDate.getTime()
  })
  const handleDelete = useCallback(() => {
    if (!templateIdToDelete) {
      return
    }
    setIsLoading(true)
    deleteTemplate({ providerId, templateId: templateIdToDelete })
      .unwrap()
      .then(() => {
        appDispatch(
          setToastContent({
            text: 'Template deleted',
            toastType: 'success',
            id: 'MessengerChat-toast-delete-success',
          }),
        )
      })
      .catch(() => {
        appDispatch(
          setToastContent({
            text: 'Failed to delete template',
            toastType: 'error',
            id: 'MessengerChat-toast-delete-error',
          }),
        )
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [appDispatch, deleteTemplate, providerId, templateIdToDelete])

  const handleSaveTemplate = useCallback(
    (values: any) => {
      setShowTemplateEditor(false)
      setIsLoading(true)
      const modifiedValues = { ...values }
      // Strip HTML attributes from tags while preserving tag names
      let sanitizedBodyValue = values.body.replace(/<(span)(?:\s+[^>]*)?>/g, '<$1>')
      sanitizedBodyValue = decodeHtmlEntities(sanitizedBodyValue)
      modifiedValues.body = sanitizedBodyValue

      templateIdToEdit && templates?.find((template) => template.id === templateIdToEdit)
        ? updateTemplate({ providerId, templateId: templateIdToEdit, data: modifiedValues })
            .unwrap()
            .then(() => {
              track({
                event: mixpanelEvents.EDIT_MESSAGE_TEMPLATE_CONFIRM,
                details: {
                  templateId: templateIdToEdit,
                },
              })
              appDispatch(
                setToastContent({
                  text: 'Template saved',
                  toastType: 'success',
                  id: 'MessengerChat-toast-save-success',
                }),
              )
            })
            .catch(() => {
              appDispatch(
                setToastContent({
                  text: 'Failed to save template',
                  toastType: 'error',
                  id: 'MessengerChat-toast-save-error',
                }),
              )
            })
            .finally(() => {
              setIsLoading(false)
            })
        : addTemplate({ providerId, data: modifiedValues })
            .unwrap()
            .then(() => {
              track({
                event: mixpanelEvents.ADD_MESSAGE_TEMPLATE_CONFIRM,
              })
              appDispatch(
                setToastContent({
                  text: 'Template saved',
                  toastType: 'success',
                  id: 'MessengerChat-toast-save-success',
                }),
              )
            })
            .catch(() => {
              appDispatch(
                setToastContent({
                  text: 'Failed to save template',
                  toastType: 'error',
                  id: 'MessengerChat-toast-save-error',
                }),
              )
            })
            .finally(() => {
              setIsLoading(false)
            })
    },
    [addTemplate, appDispatch, providerId, templateIdToEdit, templates, updateTemplate],
  )

  const handleCancel = useCallback(() => {
    setTemplateIdToEdit(null)
    setShowTemplateEditor(false)
  }, [])

  const handleSendPress = useCallback(
    (templateId: string) => {
      const bodyText = templates?.find((template) => template.id === templateId)?.body
      insertMessage(bodyText ?? '', {
        attributesToAttach: {
          message_template_id: templateId,
        },
      })
      onSendPressed()
    },
    [insertMessage, onSendPressed, templates],
  )

  const availableTabs = [
    {
      id: '1',
      label: 'Manual send',
      content: (
        <MessengerUtilityItemContentContainer>
          <MessengerUtilityItemList
            items={
              sortedTemplates?.map((template: any) => {
                return {
                  id: template.id,
                  header: template.name,
                  text: decodeHtmlEntities(template.body),
                  onDeletePress: () => {
                    track({
                      event: mixpanelEvents.DELETE_MESSAGE_TEMPLATE_ICON,
                      details: {
                        templateId: template.id,
                      },
                    })
                    setTemplateIdToDelete(template.id)
                  },
                  onEditPress: () => {
                    track({
                      event: mixpanelEvents.EDIT_MESSAGE_TEMPLATE_ICON,
                      details: {
                        templateId: template.id,
                      },
                    })
                    setTemplateIdToEdit(template.id)
                    setShowTemplateEditor(true)
                  },
                  onSendPress: () => {
                    track({
                      event: mixpanelEvents.SEND_MESSAGE_TEMPLATE_ICON,
                      details: {
                        templateId: template.id,
                      },
                    })
                    handleSendPress(template.id)
                  },
                  onPress: () => {},
                  useHoverActionButtons: true,
                }
              }) ?? []
            }
            useTokenTextInput
          />
        </MessengerUtilityItemContentContainer>
      ),
      overflowTreatment: 'scroll',
    },
    ...(isSmartTemplatesEnabled
      ? [
          {
            id: '2',
            label: 'Smart send',
            content: null, // TODO: fetch content from backend when available
          },
        ]
      : []),
  ]

  if (isLoading) {
    return <LoadingIndicator />
  }

  return showTemplateEditor ? (
    <TemplateEditor
      onCancel={handleCancel}
      onSave={handleSaveTemplate}
      initialValues={templateIdToEdit ? templates?.find((template) => template.id === templateIdToEdit) : undefined}
      isSmartTemplatesEnabled={isSmartTemplatesEnabled}
    />
  ) : (
    <MessengerUtilityItemContainer>
      <ProviderTemplateHeader
        onAddTemplatePress={() => {
          track({
            event: mixpanelEvents.ADD_MESSAGE_TEMPLATE_ENTRY_POINT,
          })
          setTemplateIdToEdit(null)
          setShowTemplateEditor(true)
        }}
        showBottomBorder={true}
      />
      <HorizontalTabsContainer contentStyle={{ flex: 1 }} tabs={availableTabs} initialTabId='1' />
      <ConfirmationModal
        visible={templateIdToDelete !== null}
        onRequestClose={() => setTemplateIdToDelete(null)}
        onConfirmationButtonPress={() => {
          track({
            event: mixpanelEvents.DELETE_MESSAGE_TEMPLATE_CONFIRM,
            details: {
              templateId: templateIdToDelete,
            },
          })
          handleDelete()
          setTemplateIdToDelete(null)
        }}
        modalTitle={
          <FormattedMessage defaultMessage='Delete template?' description='Title of modal to delete template' />
        }
        confirmationButtonText={
          <FormattedMessage defaultMessage='Delete' description='Button text to delete template' />
        }
        cancelButtonText={
          <FormattedMessage defaultMessage='Cancel' description='Button text to cancel deleting template' />
        }
        modalContents={
          <BodyText
            text={
              <FormattedMessage
                defaultMessage='Are you sure you want to delete this template? This action cannot be undone'
                description='Body text for delete confirmation modal'
              />
            }
          />
        }
        testID={'DeleteTemplateModal'}
      />
    </MessengerUtilityItemContainer>
  )
}

export default MessengerUtilityItemTemplates
